import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';

const CompareContext = createContext();

const MAX_COMPARE = 3;

export const CompareProvider = ({ children }) => {
  // Use lazy initializer to load from localStorage synchronously (StrictMode-safe)
  const [compareList, setCompareList] = useState(() => {
    try {
      const saved = localStorage.getItem('ikonic_compare');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error('Error loading compare list:', e);
      return [];
    }
  });
  
  // Track if this is the initial mount to avoid writing empty array on first render
  const isInitialMount = useRef(true);

  // Save to localStorage whenever compareList changes (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    localStorage.setItem('ikonic_compare', JSON.stringify(compareList));
  }, [compareList]);

  const addToCompare = (property) => {
    if (compareList.length >= MAX_COMPARE) {
      toast.error(`Máximo ${MAX_COMPARE} propiedades para comparar`);
      return false;
    }
    
    if (compareList.some(p => p.id === property.id)) {
      toast.info('Esta propiedad ya está en la comparación');
      return false;
    }
    
    setCompareList(prev => [...prev, {
      id: property.id,
      slug: property.slug,
      name: property.name,
      price_usd: property.price_usd,
      transaction_type: property.transaction_type,
      property_type: property.property_type,
      zone: property.zone,
      department: property.department,
      images: property.images?.slice(0, 1) || [],
      bedrooms: property.bedrooms,
      bathrooms: property.bathrooms,
      parking_spaces: property.parking_spaces,
      area_m2: property.area_m2,
      features: property.features || []
    }]);
    
    toast.success('Añadido a comparación');
    return true;
  };

  const removeFromCompare = (propertyId) => {
    setCompareList(prev => prev.filter(p => p.id !== propertyId));
  };

  const isInCompare = (propertyId) => {
    return compareList.some(p => p.id === propertyId);
  };

  const toggleCompare = (property) => {
    if (isInCompare(property.id)) {
      removeFromCompare(property.id);
      return false;
    } else {
      return addToCompare(property);
    }
  };

  const clearCompare = () => {
    setCompareList([]);
  };

  return (
    <CompareContext.Provider value={{
      compareList,
      addToCompare,
      removeFromCompare,
      isInCompare,
      toggleCompare,
      clearCompare,
      compareCount: compareList.length,
      maxCompare: MAX_COMPARE
    }}>
      {children}
    </CompareContext.Provider>
  );
};

export const useCompare = () => {
  const context = useContext(CompareContext);
  if (!context) {
    throw new Error('useCompare must be used within a CompareProvider');
  }
  return context;
};
